import React from 'react';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';
import { Button } from './Button';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: (props: { error: Error; reset: () => void }) => React.ReactNode;
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Single top-level ErrorBoundary. Catches render/hook errors so a single
 * component crash doesn't white-screen the whole SPA. Wrap at the root
 * (and optionally around large sub-trees that may fail independently).
 */
export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (this.props.onError) {
      this.props.onError(error, info);
    } else if (process.env.NODE_ENV !== 'production') {
      console.error('[ErrorBoundary] caught render error:', error, info);
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset });
    }

    return (
      <div
        role="alert"
        className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-900/60 px-4"
      >
        <div className="w-full max-w-md rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-8 text-center shadow-card">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger-50 text-danger-600">
            <AlertTriangle className="h-6 w-6" aria-hidden="true" />
          </div>
          <h1 className="text-h2 text-neutral-900 dark:text-neutral-100">Something went wrong</h1>
          <p className="mt-2 text-body text-neutral-600 dark:text-neutral-400">
            An unexpected error prevented this screen from loading. Our team has
            been notified. You can try again, or head back to the dashboard.
          </p>
          {process.env.NODE_ENV !== 'production' && (
            <pre className="mt-4 max-h-40 overflow-auto rounded-md bg-neutral-100 dark:bg-neutral-800 p-3 text-left text-caption text-neutral-700 dark:text-neutral-300">
              {error.message}
            </pre>
          )}
          <div className="mt-6 flex items-center justify-center gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                this.reset();
                window.location.href = '/';
              }}
              leftIcon={<Home className="h-4 w-4" aria-hidden="true" />}
            >
              Go home
            </Button>
            <Button
              variant="primary"
              onClick={() => window.location.reload()}
              leftIcon={<RefreshCcw className="h-4 w-4" aria-hidden="true" />}
            >
              Reload
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
